import { useState, useCallback } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { Textfield } from '@simplywallst/ui-core';
import { SearchIcon } from '@/components/Search';
import loadable from '@loadable/component';
import { SearchResultLink } from './styled';
import Modal from '@/components/Modal/components/Modal';
import { useDispatch } from 'react-redux';
import { cacheResults } from '@/components/Search/redux/routines';
import { useFlag } from '@/features/unleash';
import { useTranslation } from '@/hooks/useTranslation';
import AlgoliaSearchLoading from '@/components/Search/components/AlgoliaSearch/Loading';
import LoadingDefaultState from '@/components/Search/components/DefaultState/Loading';
import { SUPPORTED_LOCALES } from '@/utils/locale';
import { ROUTE_LOCALE_COMPANY } from '@/constants/routes';
import { pathToAction } from 'redux-first-router';
import routesGenerator from '@/router/routesGenerator';
import { useLocale } from '@/hooks/useLocale';

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch'),
  {
    fallback: <AlgoliaSearchLoading />,
  }
);

const DefaultState = loadable(
  () => import('@/components/Search/components/DefaultState'),
  {
    fallback: <LoadingDefaultState />,
  }
);

const routesMap = routesGenerator();

export const Search = () => {
  const flag = useFlag({ name: 'mono-default-state' });
  const hasDefaultSearchState = !flag.flagsReady || flag.enabled;
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const dispatch = useDispatch();
  const t = useTranslation();
  const locale = useLocale();

  const primaryWrapperHandler = useCallback<
    NonNullable<
      ComponentPropsWithoutRef<typeof AlgoliaSearch>['primaryWrapper']
    >
  >(
    (child, { canonicalUrl }) => {
      if (
        SUPPORTED_LOCALES.includes(locale as (typeof SUPPORTED_LOCALES)[number])
      ) {
        const targetRoute = pathToAction(canonicalUrl, routesMap);
        const payloadWithLocale = {
          ...targetRoute,
          type: ROUTE_LOCALE_COMPANY,
          payload: {
            ...targetRoute.payload,
            locale,
          },
        };
        return (
          <SearchResultLink
            to={payloadWithLocale}
            onClick={() => setShowSearch(false)}
          >
            {child}
          </SearchResultLink>
        );
      }

      return (
        <SearchResultLink
          to={canonicalUrl}
          onClick={() => setShowSearch(false)}
        >
          {child}
        </SearchResultLink>
      );
    },
    [locale]
  );

  const secondaryWrapperHandler = useCallback<
    NonNullable<
      ComponentPropsWithoutRef<typeof AlgoliaSearch>['secondaryWrapper']
    >
  >(
    (child, { canonicalUrl }) => {
      if (
        SUPPORTED_LOCALES.includes(locale as (typeof SUPPORTED_LOCALES)[number])
      ) {
        const targetRoute = pathToAction(canonicalUrl, routesMap);
        const payloadWithLocale = {
          ...targetRoute,
          type: ROUTE_LOCALE_COMPANY,
          payload: {
            ...targetRoute.payload,
            locale,
          },
        };
        return (
          <SearchResultLink
            to={payloadWithLocale}
            onClick={() => setShowSearch(false)}
          >
            {child}
          </SearchResultLink>
        );
      }
      return (
        <SearchResultLink
          to={canonicalUrl}
          onClick={() => setShowSearch(false)}
        >
          {child}
        </SearchResultLink>
      );
    },
    [locale]
  );

  return (
    <>
      <Textfield
        type="text"
        name="company_search"
        prefix={<SearchIcon />}
        readOnly={true}
        placeholder={t('app.topbar.nav.search.placeholder')}
        onClick={() => {
          setShowSearch(true);
        }}
        onFocus={() => {
          setShowSearch(true);
        }}
        value={query}
      />
      {showSearch && (
        <Modal
          isNaked
          onSecondaryClick={() => {
            setShowSearch(false);
          }}
        >
          <AlgoliaSearch
            prefix={<SearchIcon />}
            onClose={() => setShowSearch(false)}
            onSearchResult={(results, query) => {
              setQuery(query);
              dispatch(cacheResults.trigger(results));
            }}
            onReset={() => setQuery('')}
            query={query}
            primaryWrapper={primaryWrapperHandler}
            secondaryWrapper={secondaryWrapperHandler}
            defaultState={
              hasDefaultSearchState ? (
                <DefaultState onClick={() => setShowSearch(false)} />
              ) : undefined
            }
          />
        </Modal>
      )}
    </>
  );
};
